import { useEffect, useRef, useState } from "react";
import {
  FiArrowLeft,
  FiArrowRight,
  FiCheck,
  FiGrid,
  FiPlayCircle,
  FiShoppingBag,
  FiX,
} from "react-icons/fi";

import "./style.css";

const FLOWS = {
  orders: {
    label: "Pedidos",
    icon: FiShoppingBag,
    video: "https://youtu.be/Tz63cvZ6GIE",
    steps: [
      {
        title: "Tudo importante em uma única visão",
        description: "Ao criar ou abrir um pedido, você encontra cliente, itens, resumo e pagamento na mesma tela.",
        tip: "Use os atalhos destacados para editar o cliente, adicionar produtos ou lançar o pagamento.",
        image: "/imagens-tutorial/1.png",
      },
      {
        title: "Encontre o botão Adicionar itens",
        description: "No quadro Itens do pedido, clique no botão laranja Adicionar itens, no canto superior direito.",
        tip: "Esse botão abre o catálogo sem sair do pedido que está sendo editado.",
        image: "/imagens-tutorial/1.png",
        focus: "add-items",
      },
      {
        title: "Escolha os produtos do pedido",
        description: "Selecione uma categoria e pesquise o produto desejado no catálogo.",
        tip: "Depois de configurar quantidade, adicionais e observações, clique em Adicionar produto.",
        image: "/imagens-tutorial/2.png",
      },
      {
        title: "Confirme os itens adicionados",
        description: "Produtos novos aparecem primeiro na área Itens não confirmados.",
        tip: "Clique em Confirmar todos para gravar os itens no pedido e enviá-los para impressão, quando configurada.",
        image: "/imagens-tutorial/3.png",
      },
      {
        title: "Abra os dados do cliente",
        description: "Na parte superior do pedido, clique sobre o nome do cliente ou no ícone de edição ao lado.",
        tip: "A área destacada abre o formulário de cliente e endereço desse pedido.",
        image: "/imagens-tutorial/1.png",
        focus: "customer",
      },
      {
        title: "Atualize cliente e endereço",
        description: "Altere nome, telefone e dados de entrega diretamente no formulário.",
        tip: "Não existe botão Salvar: as alterações são gravadas automaticamente após a digitação.",
        image: "/imagens-tutorial/4.png",
      },
      {
        title: "Lance um ou mais pagamentos",
        description: "Escolha a forma, informe o valor e clique em Adicionar. Você pode dividir o pagamento.",
        tip: "Confira o valor Restante antes de usar Lançar no caixa para finalizar.",
        image: "/imagens-tutorial/5.png",
      },
    ],
  },
  tables: {
    label: "Mesas e comandas",
    icon: FiGrid,
    video: "https://youtu.be/zaPuCQ_vJuk",
    steps: [
      {
        title: "Inicie o atendimento da mesa",
        description: "Abra uma mesa livre para visualizar o resumo, as comandas e as ações disponíveis.",
        tip: "A lateral agora reúne total, permanência, comandas e pagamento da mesa.",
        image: "/imagens-tutorial/mesa - 1.png",
      },
      {
        title: "Escolha como iniciar o atendimento",
        description: "Use Novo pedido para começar rapidamente ou Nova comanda para identificar o atendimento por código ou nome.",
        tip: "Nova comanda é ideal quando várias pessoas da mesma mesa precisam manter pedidos separados.",
        image: "/imagens-tutorial/mesa - 1.png",
        focus: "table-actions",
        focusLabel: "Escolha uma opção",
      },
      {
        title: "Identifique cada comanda",
        description: "Ao escolher Nova comanda, informe um número, código ou o nome do cliente para organizar o pedido.",
        tip: "Exemplos: 12, A15 ou Matheus. A comanda criada será selecionada automaticamente.",
        image: "/imagens-tutorial/mesa - 2.png",
      },
      {
        title: "Selecione a comanda criada",
        description: "A comanda aparece na lateral e fica selecionada para você editar cliente, adicionar itens e lançar pagamentos.",
        tip: "Use Nova comanda quando precisar separar os pedidos de mais pessoas na mesma mesa.",
        image: "/imagens-tutorial/mesa - 3.png",
      },
      {
        title: "Abra os dados do cliente da comanda",
        description: "Na parte superior, clique sobre o nome Pedido de mesa ou no ícone de edição ao lado.",
        tip: "A edição de cliente funciona da mesma forma para pedidos de mesa, delivery e balcão.",
        image: "/imagens-tutorial/mesa - 3.png",
        focus: "table-customer",
      },
      {
        title: "Edite o cliente da comanda",
        description: "Informe nome, telefone e os demais dados necessários para identificar o cliente.",
        tip: "Não existe botão Salvar: as alterações são gravadas automaticamente após a digitação.",
        image: "/imagens-tutorial/4.png",
      },
      {
        title: "Confirme os itens da comanda",
        description: "Assim como nos outros pedidos, produtos novos aparecem primeiro em Itens não confirmados.",
        tip: "Confirme os itens antes de finalizar a comanda. Quando todos os atendimentos terminarem, use Fechar mesa.",
        image: "/imagens-tutorial/mesa - 4.png",
      },
    ],
  },
};

export function OrderUpdatesTour({ open, onClose, initialFlow = "orders" }) {
  const [flow, setFlow] = useState(initialFlow);
  const [step, setStep] = useState(0);
  const [expandedImage, setExpandedImage] = useState("");
  const closeButtonRef = useRef(null);

  useEffect(() => {
    if (!open) return;

    setFlow(initialFlow);
    setStep(0);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeButtonRef.current?.focus();

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [initialFlow, open]);

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = event => {
      if (event.key !== "Escape") return;
      if (expandedImage) return setExpandedImage("");
      onClose();
    };
    document.addEventListener("keydown", handleKeyDown);

    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [expandedImage, onClose, open]);

  if (!open) return null;

  const currentFlow = FLOWS[flow];
  const currentStep = currentFlow.steps[step];
  const isLastStep = step === currentFlow.steps.length - 1;

  function selectFlow(nextFlow) {
    setFlow(nextFlow);
    setStep(0);
    setExpandedImage("");
  }

  return (
    <div className="order-updates-backdrop" onMouseDown={event => event.target === event.currentTarget && onClose()}>
      <section className="order-updates-modal" role="dialog" aria-modal="true" aria-labelledby="order-updates-title">
        <header className="order-updates-header">
          <div><span className="order-updates-badge">NOVIDADES</span><h2 id="order-updates-title">Conheça o novo fluxo de pedidos</h2><p>Veja onde encontrar as principais ações da operação.</p></div>
          <button ref={closeButtonRef} type="button" className="order-updates-close" onClick={onClose} aria-label="Fechar tutorial"><FiX /></button>
        </header>

        <div className="order-updates-tabs" role="tablist" aria-label="Escolha um fluxo">
          {Object.entries(FLOWS).map(([key, item]) => {
            const Icon = item.icon;
            return <button key={key} type="button" role="tab" aria-selected={flow === key} className={flow === key ? "active" : ""} onClick={() => selectFlow(key)}><Icon /> {item.label}</button>;
          })}
        </div>

        <div className="order-updates-content">
          <div className="order-updates-copy">
            <span className="order-updates-step">ETAPA {step + 1} DE {currentFlow.steps.length}</span>
            <h3>{currentStep.title}</h3>
            <p>{currentStep.description}</p>
            <div className="order-updates-tip"><FiCheck /><span>{currentStep.tip}</span></div>
            <a href={currentFlow.video} target="_blank" rel="noreferrer"><FiPlayCircle /> Assistir tutorial completo</a>
          </div>
          <button type="button" className={`order-updates-visual ${currentStep.focus ? `focus-${currentStep.focus}` : ""}`} onClick={() => setExpandedImage(currentStep.image)} aria-label="Ampliar imagem da etapa">
            <img src={currentStep.image} alt={currentStep.title} />
            {currentStep.focus && <mark className="order-updates-focus-target" data-label={currentStep.focusLabel || "Clique aqui"}>Clique aqui</mark>}
            <span>Clique para ampliar</span>
          </button>
        </div>

        <footer className="order-updates-footer">
          <div className="order-updates-dots" aria-label={`Etapa ${step + 1} de ${currentFlow.steps.length}`}>
            {currentFlow.steps.map((item, index) => <button key={item.title} type="button" className={index === step ? "active" : ""} onClick={() => setStep(index)} aria-label={`Ir para etapa ${index + 1}`} />)}
          </div>
          <div className="order-updates-actions">
            {step === 0 ? <button type="button" className="skip" onClick={onClose}>Pular</button> : <button type="button" className="previous" onClick={() => setStep(step - 1)}><FiArrowLeft /> Anterior</button>}
            <button type="button" className="next" onClick={() => isLastStep ? onClose() : setStep(step + 1)}>{isLastStep ? "Concluir" : "Próximo"}{!isLastStep && <FiArrowRight />}</button>
          </div>
        </footer>
      </section>
      {expandedImage && (
        <div className="order-updates-lightbox" role="dialog" aria-modal="true" aria-label="Imagem ampliada" onMouseDown={event => event.target === event.currentTarget && setExpandedImage("")}>
          <button type="button" onClick={() => setExpandedImage("")} aria-label="Fechar imagem ampliada"><FiX /></button>
          <img src={expandedImage} alt={currentStep.title} />
        </div>
      )}
    </div>
  );
}
